import { Dropdown } from '../../components/Dropdown';
import { InfoNote } from '../../components/InfoNote';
import { SectionHeader } from '../../components/SectionHeader';
import { TimeChangeIcon } from '../../icons/TimeChangeIcon';
import {
  OptionCard,
  OptionImage,
  OptionImagePlaceholder,
  OptionLabel,
  OptionsSection,
  Panel,
  SelectionCount,
  TimeslotHeading,
  TimeslotPanel,
} from './SingleOptionTimeslotPanel.styles';
import type { SingleOptionTimeslotPanelProps } from './SingleOptionTimeslotPanel.types';

const defaultNoteIcon = <TimeChangeIcon size={18} />;

export function SingleOptionTimeslotPanel({
  title = 'Options.',
  subtitle = 'Choose your times.',
  options,
  selectedOptionId,
  selectedTimeslot,
  selectionCountText,
  onOptionSelect,
  onTimeslotChange,
  noteText,
  noteIcon = defaultNoteIcon,
  timeslotPlaceholder = 'Time*',
  timeslotHeading = 'What time would you like?',
  timeslotAriaLabelFormatter = (l) => `${l} time`,
}: SingleOptionTimeslotPanelProps) {
  const selectedOption = options.find((o) => o.id === selectedOptionId);
  const showTimeslotPanel = selectedOption && selectedOption.timeslots.length > 0;

  const items: React.ReactNode[] = [];

  options.forEach((option) => {
    const isSelected = option.id === selectedOptionId;
    const isDisabled = !!option.disabled;

    items.push(
      <OptionCard
        key={option.id}
        type="button"
        data-selected={isSelected}
        data-disabled={isDisabled}
        aria-pressed={isSelected}
        aria-disabled={isDisabled}
        disabled={isDisabled}
        onClick={() => onOptionSelect(option.id)}
      >
        <OptionLabel>{option.label}</OptionLabel>
        {option.imageUrl ? (
          <OptionImage src={option.imageUrl} alt={option.imageAlt || option.label} />
        ) : (
          <OptionImagePlaceholder />
        )}
      </OptionCard>
    );

    if (isSelected && showTimeslotPanel) {
      items.push(
        <TimeslotPanel key={`${option.id}-timeslot`}>
          <TimeslotHeading>{timeslotHeading}</TimeslotHeading>
          <Dropdown
            options={selectedOption!.timeslots}
            value={selectedTimeslot}
            placeholder={timeslotPlaceholder}
            onChange={onTimeslotChange}
            aria-label={timeslotAriaLabelFormatter(option.label)}
          />
          {noteText && <InfoNote text={noteText} icon={noteIcon} />}
        </TimeslotPanel>
      );
    }
  });

  return (
    <Panel>
      <SectionHeader title={title} subtitle={subtitle} />
      {selectionCountText && (
        <SelectionCount aria-live="polite">{selectionCountText}</SelectionCount>
      )}
      <OptionsSection>{items}</OptionsSection>
    </Panel>
  );
}
